import { boundingBox } from '../../common/pdfReport';

/**
 * Describes an intertidal analysis as a document.
 * The result here is a set of waterlines, each one an image paired with the tide level at the
 * moment it was taken. That pairing is the whole method: a waterline without its tide says where
 * the water was but not what that means, and the elevation model built from them is only as good as
 * the spread of tide levels that went in. So the report leads with the tide range covered and lists
 * every waterline with its own level, rather than summarising them away.
 */

/**
 * @constant REPORT_MODULE
 * @description Module key, used to name the file and to register the export.
 * @memberof module:Intertidal
 */
export const REPORT_MODULE = 'intertidal';

/**
 * @function tideValueOf
 * @description One layer's tide level, whichever of the two names it arrived under.
 * @param {Object} layer - A waterline layer.
 * @memberof module:Intertidal
 * @returns {Number|null}
 */
export const tideValueOf = (layer) => {
  const value = Number(layer?.tideValue ?? layer?.tide);
  return Number.isFinite(value) ? value : null;
};

/**
 * @function tideRangeOf
 * @description The lowest and highest tide among the waterlines, and the span between them.
 * The span is the part that matters: it is the vertical interval the elevation model is
 * interpolated over, and anything outside it is extrapolation.
 * @param {Array<Object>} [layers] - The waterline layers.
 * @param {Object} text - `{ range, span }`, translated, taking `{{low}}`, `{{high}}` and
 *  `{{span}}`.
 * @memberof module:Intertidal
 * @returns {Object} `{ range, span }`, each `null` when no layer carries a tide.
 */
export const tideRangeOf = (layers, text) => {
  const tides = (layers || []).map(tideValueOf).filter((value) => value !== null);
  if (!tides.length) return { range: null, span: null };
  const low = Math.min(...tides);
  const high = Math.max(...tides);
  return {
    range: text.range({ low: low.toFixed(2), high: high.toFixed(2) }),
    span: text.span({ span: (high - low).toFixed(2) }),
  };
};

/**
 * @function waterlineTable
 * @description One row per waterline: its date and the tide level at that moment, sorted by tide
 * so the coverage of the range reads off the table directly.
 * @param {Array<Object>} [layers] - The waterline layers.
 * @param {Object} text - `{ columns: { date, tide } }`, already translated.
 * @memberof module:Intertidal
 * @returns {Object} A table for {@link renderReport}.
 */
export const waterlineTable = (layers, text) => ({
  columns: [text.columns.date, text.columns.tide],
  rows: (layers || [])
    .map((layer) => ({ layer, tide: tideValueOf(layer) }))
    .sort((a, b) => (a.tide ?? 0) - (b.tide ?? 0))
    .map(({ layer, tide }) => {
      const when = layer?.date == null ? null : new Date(Number(layer.date) || layer.date);
      return [
        when && !Number.isNaN(when.getTime()) ? when.toISOString().slice(0, 10) : String(layer?.date ?? ''),
        tide === null ? null : tide.toFixed(2),
      ];
    }),
});

/**
 * @function buildIntertidalReport
 * @description The whole document, as data.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring.
 * @param {Array<Object>} [params.waterlineLayers] - The waterlines, each with a date and a tide.
 * @param {String} [params.kmlFileName] - The uploaded area file.
 * @param {String} [params.tideFileName] - The tide gauge file.
 * @param {Array<Object>} [params.images] - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Every piece of wording, already translated.
 * **Called by:
 * @see {ProcessingPage} - the intertidal result page.
 * @memberof module:Intertidal
 * @returns {Object} A document description for {@link renderReport}.
 */
export const buildIntertidalReport = ({ coordinates, waterlineLayers, kmlFileName, tideFileName, images, text }) => {
  const tide = tideRangeOf(waterlineLayers, text.tide);
  const withTide = (waterlineLayers || []).filter((layer) => tideValueOf(layer) !== null).length;

  return {
    title: text.title,
    subtitle: text.subtitle,
    footer: text.footer,
    missingImageNote: text.missingImage,
    sections: [
      {
        heading: text.areaHeading,
        rows: [
          [text.areaFile, kmlFileName],
          [text.areaExtent, boundingBox(coordinates)],
          [text.tideFile, tideFileName],
        ],
      },
      {
        heading: text.waterlineHeading,
        rows: [
          [text.waterlineCount, waterlineLayers?.length || null],
          [text.withTideCount, withTide || null],
          [text.tideRange, tide.range],
          [text.tideSpan, tide.span],
        ],
        /**
         * A waterline that never got a tide level cannot contribute to the elevation model, so the
         * gap is called out rather than left for the reader to spot by subtracting two counts.
         */
        note:
          waterlineLayers?.length && withTide < waterlineLayers.length
            ? text.missingTideNote({ missing: waterlineLayers.length - withTide })
            : text.waterlineNote,
      },
      {
        heading: text.tableHeading,
        table: waterlineTable(waterlineLayers, text.waterlines),
      },
      { heading: text.mapsHeading, images: images || [] },
    ],
  };
};
